<script setup>
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';

import { useAuthStore, useAlertStore, useConfigStore } from '@lib/pp-shop-lib/stores';
import { Alert } from '@lib/pp-shop-lib/components';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const router = useRouter();
const configStore = useConfigStore();

const schema = Yup.object().shape({
    firstName: Yup.string()
        .required(t('auth.validation.firstNameRequired')),
    lastName: Yup.string()
        .required(t('auth.validation.lastNameRequired')),
    email: Yup.string()
        .required(t('auth.validation.emailRequired')),
    password: Yup.string()
        .required(t('auth.validation.passwordRequired'))
        .min(6, t('auth.validation.passwordMin', { min: 6 })),
    passwordRepeat: Yup.string()
        .required(t('auth.resetPassword.validation.passwordRepeatRequired'))
        .oneOf([Yup.ref('password')], t('auth.resetPassword.validation.passwordMismatch')),
    website: Yup.string().notRequired()
});

async function onSubmit(values) {
    const authStore = useAuthStore();
    const alertStore = useAlertStore();
    alertStore.clear('register');
    try {
        const { firstName, lastName, email, password, website } = values;
        const registerPayload = { firstName, lastName, email, password, website, app: configStore.app };
        let response = await authStore.register(registerPayload);
        if (!response || !response.ok) {
            throw response?.error || new Error(response?.message || t('auth.registration.failed'));
        }
        // Automatically log in the user after successful registration 
        let result = await authStore.login(email, password, 'register');
        if (result?.kid) {
            const redirectPath = configStore.registrationSuccessRedirect || configStore.baseRedirect || configStore.baseDir || '/shop';
            router.push(redirectPath)
        }
        alertStore.success(t('auth.registration.successful'));
    } catch (error) {
        alertStore.error(error, 'register');
    }
}
</script>

<template>
    <div class="uk-card uk-card-body uk-align-center">
        <h4 class="uk-card-title">{{ $t('auth.registerTitle') }}</h4>
        <Alert scope="register" />
        <div class="">
            <Form class="uk-form-horizontal uk-margin-large" @submit="onSubmit" :validation-schema="schema"
                v-slot="{ errors, isSubmitting }">
                <div class="uk-margin">
                    <label class="uk-form-label">{{ $t('auth.fields.firstName') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" name="firstName" type="text"
                            :class="{ 'is-invalid': errors.firstName }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.firstName }}</div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">{{ $t('auth.fields.lastName') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" name="lastName" type="text"
                            :class="{ 'is-invalid': errors.lastName }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.lastName }}</div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">{{ $t('auth.fields.email') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" name="email" type="text"
                            :class="{ 'is-invalid': errors.email }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.email }}</div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">{{ $t('auth.fields.password') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" name="password" type="password"
                            :class="{ 'is-invalid': errors.password }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.password }}</div>
                </div>
                <div class="uk-margin">
                    <label class="uk-form-label">{{ $t('auth.resetPassword.passwordRepeat') }}</label>
                    <div class="uk-form-controls">
                        <Field class="uk-input" name="passwordRepeat" type="password"
                            :class="{ 'is-invalid': errors.passwordRepeat }" />
                    </div>
                    <div class="uk-form-danger">{{ errors.passwordRepeat }}</div>
                </div>
                <div class="register-honeypot" aria-hidden="true">
                    <label for="register-website">Website</label>
                    <Field id="register-website" name="website" type="text" autocomplete="off" tabindex="-1" />
                </div>
                <Field name="app" type="hidden" :value="configStore.app" />
                <div class="uk-margin">
                    <button class="uk-button uk-button-default uk-button-small" :disabled="isSubmitting">
                        <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                        {{ $t('auth.createAccount') }}
                    </button>
                    <router-link to="/shop/account/login"
                        class="uk-button uk-button-default uk-button-small uk-margin-small-left">{{ $t('auth.cancel') }}</router-link>
                </div>
            </Form>
        </div>
    </div>
</template>

<style scoped>
.register-honeypot {
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
}
</style>
